/**
 * Pie component module.
 * @module shared/components/Pie
 *
 */
import React from 'react';
import PropTypes from 'prop-types';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import Row from '../Row';
import {withTheme} from 'styled-components/macro';
import {withSize} from 'react-sizeme';
import Column from '../Column';
import {Table} from '../../index';
import {ColorBox} from '../../utils/colors';
import LinkedText from '../LinkedText';
import InLineText from '../InLineText';
import {v4 as uuid} from 'uuid';

export const colorRender = row => <><ColorBox color={row.color}/>{row.name}</>;

const Pie = ({
  title,
  data,
  sliceAction,
  theme,
  size,
  centerAction,
  hideLegend,
  toolTipFormatter,
  table = {
    defaultSort: {
      column: 'y',
      desc: true,
    },
    columns: [
      {
        title: 'Name',
        dataPoint: 'name',
        type: 'text',
        render: colorRender,
        minWidth: 120,
        isSortable: true,
        verticalAlign: true,
      },
      {
        title: 'Value',
        dataPoint: 'y',
        type: 'number',
        minWidth: 90,
        isSortable: true,
      },
    ],
  },
}) => {
  const options = React.useMemo(() => ({
    title: {
      text: '',
    },
    key: uuid(),
    series: [{data}],
    useHighStocks: false,
    legend: {enabled: false},
    credits: {enabled: false},
    chart: {
      type: 'pie',
      backgroundColor: null,
      plotBorderWidth: 0,
      plotShadow: false,
      zoomType: null,
      events: {
        click: centerAction,
      },
      style: {
        cursor: centerAction ? 'pointer' : 'default',
      },
      margin: [0, 0, 0, 0],
    },
    tooltip: {
      backgroundColor: '#FFFFFF',
      borderColor: '#FFFFFF',
      useHTML: true,
      formatter: toolTipFormatter || function formatTooltip() { // ETODO Improve default formatter or add types for formatters that and standardized
        let p = this.point || this.points[0];
        if (['No Alerts', 'No Rebalancing Proposals', 'No Risk Alerts', 'No Institutions'].indexOf(p.name) < 0) {
          return '<div>'
            + p.name.substr(0, 50)
            + ': </div><div style="font-size:13px;margin-top:3px;" class="text-center">'
            + p.y
            + '</div>';
        }

        return '<div>' + p.name.substr(0, 50) + '</div>';
      },
      followTouchMove: false,
      style: {
        fontSize: '10px',
        fontFamily: 'ProximaBold, sans-serif',
      },
    },
    plotOptions: {
      pie: {
        allowPointSelect: false,
        cursor: sliceAction ? 'pointer' : 'default',
        borderWidth: 0,
        startAngle: 0,
        dataLabels: {
          enabled: false,
        },
        showInLegend: true,
        point: {
          events: {
            legendItemClick: function legendItemClick(e) {
              e.preventDefault();
            },
          },
        },
      },
      series: {
        cursor: sliceAction && 'pointer' || 'default',
        size: '100%',
        innerSize: '50%',
        minSize: 120,
        point: {
          events: {
            click: sliceAction && (slice => sliceAction(slice.point)),
          },
        },
      },
    },
  }), [data]);

  let layout = 'sm';
  let Direction = Row;
  let InnerDirection = Row;
  let containerHeight = '100%';
  let containerWidth = '100%';
  if (size.height >= 300) {
    if (size.width > 620) {
      Direction = Column;
      containerHeight = '100%';
      containerWidth = '100%';
      layout = 'lg';
    } else if (size.width > 380) {
      Direction = Row;
      InnerDirection = Row;
      containerHeight = '100%';
      containerWidth = '100%';
      layout = 'md';
    } else {
      Direction = Row;
      InnerDirection = Column;
      layout = 'md';
    }
  } else if (size.width >= 600) {
    Direction = Row;
    containerHeight = '100%';
    containerWidth = '170px';
    layout = 'lg';
  } else if (size.width >= 300) {
    Direction = Row;
    InnerDirection = Column;
    containerHeight = '100%';
    containerWidth = '170px';
    layout = 'md';
  }

  return <Direction height={'100%'} width={'100%'}>
    <Column height={containerHeight} maxHeight={'190px'} width={containerWidth} maxWidth={'190px'} >
      <HighchartsReact
        key={options.key.concat(size.width)}
        containerProps={{style: {height: '100%', width: '100%', ...(theme.neumorphic && {filter: 'drop-shadow(3px 3px 4px rgba(0, 0, 0, 0.5))'})}}}
        highcharts={Highcharts}
        options={options}
      />
    </Column>
    {layout === 'md' && !hideLegend && <InnerDirection width={'fit-content'}
                                                       layout={InnerDirection === Column ? null : {grow: 1}}
                                                       wrap={true}
                                                       justifySubRow={'space around'}
                                                       alignSubRow={InnerDirection === Column && 'left'}
                                                       alignItems={InnerDirection === Column && 'left'}>
      {data.slice(0, 4).filter(i => !!i)
        .map(i => <div key={`${title}-${i.name}`}>
          {sliceAction
            ? <LinkedText colorize={false} fontSize={'12px'} verticalAlign={true} action={() => sliceAction(i)}>
              <ColorBox color={i.color}/>
              {i.name}
            </LinkedText>
            : <InLineText fontSize={'12px'} verticalAlign={true}>
              <ColorBox color={i.color}/>
              {i.name}
            </InLineText>
          }
        </div>)}
    </InnerDirection>}
    {table && layout === 'lg' && !hideLegend && <Column height={Direction === Row ? '100%' : null}
                                                        width={Direction === Row ? '50px' : null}
                                                        layout={{grow: 1}}>
      <Table height={'50px'}
             dataSet={data.filter(d => !!d[table.columns[0].dataPoint])}
             defaultSort={table.defaultSort}
             rowOnClick={sliceAction}
             columns={table.columns}/></Column>}
  </Direction>;
};

export default withSize({monitorHeight: true})(withTheme(Pie));

Pie.propTypes = {
  toolTipFormatter: PropTypes.func,
  title: PropTypes.string,
  /**
   * From SizeMe
   */
  size: PropTypes.object,
  // Y-Axis Scale
  // Number of Y-Axis Markers
  // X-Axis Scale
  // Number of X-Axis Markers
  /**
   * Optional center click action
   */
  centerAction: PropTypes.func,
  /**
   * Optional function to call on click of a slice
   */
  sliceAction: PropTypes.func,
  data: PropTypes.array,
  hideLegend: PropTypes.bool,
  /**
   * Forwarded to table
   */
  table: PropTypes.shape({
    defaultSort: PropTypes.object,
    columns: PropTypes.array,
  }),
  theme: PropTypes.object,
};
